// src/pages/PrintMenuPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getToken } from "../services/authService";
import "./print-menu.css";
import logo from "../assets/logo-melonares.png";

// lee campo base_es/base_en o base
function pick(obj, base, lang) {
    const key = lang === "en" ? `${base}_en` : `${base}_es`;
    if (obj && obj[key] && String(obj[key]).trim()) return obj[key];
    return (obj && obj[base]) || "";
}

// devuelve ambos idiomas con fallback a base
function pickBoth(obj, base) {
    const es = (obj?.[`${base}_es`] ?? obj?.[base] ?? "").toString().trim();
    const en = (obj?.[`${base}_en`] ?? obj?.[base] ?? "").toString().trim();
    return { es, en };
}

// Traducciones de cursos (fallback si no existen *_en en backend)
const COURSE_EN_MAP = {
    "primer plato": "First course",
    "segundo plato": "Second course",
    "postre": "Dessert",
};

const ALLERGEN_EN_MAP = {
    "gluten": "Gluten",
    "trigo": "Wheat",
    "crustaceos": "Crustaceans",
    "crustáceos": "Crustaceans",
    "marisco": "Crustaceans",
    "mariscos": "Crustaceans",
    "pescado": "Fish",
    "huevo": "Eggs",
    "huevos": "Eggs",
    "cacahuete": "Peanuts",
    "cacahuetes": "Peanuts",
    "maní": "Peanuts",
    "mani": "Peanuts",
    "soja": "Soy",
    "lacteos": "Milk",
    "lácteos": "Milk",
    "leche": "Milk",
    "frutos con cascaras": "Tree nuts",
    "apio": "Celery",
    "mostaza": "Mustard",
    "sésamo": "Sesame",
    "sesamo": "Sesame",
    "sulfitos": "Sulphites",
    "dióxido de azufre": "Sulphur dioxide",
    "anhídrido sulfuroso": "Sulphur dioxide",
    "altramuces": "Lupin",
    "altramuz": "Lupin",
    "moluscos": "Molluscs",
};

// grupos en ambos idiomas con fallback por posición
function groupForBoth(p, idx, total) {
    let es =
        (pick(p, "curso", "es").trim()) ||
        (pick(p, "categoria", "es").trim()) ||
        (pick(p, "tipo", "es").trim());

    let en =
        (pick(p, "curso", "en").trim()) ||
        (pick(p, "categoria", "en").trim()) ||
        (pick(p, "tipo", "en").trim());

    if (!es) es = defaultGroupByIndex(idx, total, "es");
    if (!en) {
        const raw = defaultGroupByIndex(idx, total, "en");
        const key = raw.toLowerCase();
        en = COURSE_EN_MAP[key] || raw;
    }
    return { es, en };
}

function defaultGroupByIndex(index, total, lang) {
    if (lang === "en") {
        if (total === 1) return "Main";
        if (total === 2) return index === 0 ? "First course" : "Second course";
        if (index === 0) return "First course";
        if (index === total - 1) return "Dessert";
        if (index === 1) return "Second course";
        if (index === 2) return "Second course";
        return `Course ${index}`;
    } else {
        if (total === 1) return "Principal";
        if (total === 2) return index === 0 ? "Primer plato" : "Segundo plato";
        if (index === 0) return "Primer plato";
        if (index === total - 1) return "Postre";
        if (index === 1) return "Segundo plato";
        if (index === 2) return "Segundo plato";
        return `Plato ${index + 1}`;
    }
}

const norm = (s) =>
    String(s || "")
        .normalize("NFD")
        .replace(/\p{Diacritic}/gu, "")
        .toLowerCase()
        .trim();

export default function PrintMenuPage() {
    const [params] = useSearchParams();
    const lang = params.get("lang") === "en" ? "en" : "es";
    const eventId = params.get("eventId");
    const menuId = params.get("menuId");

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [evento, setEvento] = useState(null);

    // Espera a que las imágenes dentro de .sheet estén listas antes de imprimir
    const [assetsReady, setAssetsReady] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            setLoading(true);
            setError("");
            try {
                const headers = { Authorization: `Bearer ${getToken()}` };

                // ideal: detalle /:id, fallback: /all
                let res = await fetch(`${process.env.REACT_APP_API_URL}/api/eventos/${eventId}`, { headers });
                if (!res.ok) {
                    const allRes = await fetch(`${process.env.REACT_APP_API_URL}/api/eventos/all`, { headers });
                    const all = await allRes.json();
                    const ev = (all || []).find((e) => String(e.id) === String(eventId));
                    if (!cancelled) setEvento(ev || null);
                } else {
                    const ev = await res.json();
                    if (!cancelled) setEvento(ev);
                }
            } catch (e) {
                if (!cancelled) setError(lang === "en" ? "Error loading data" : "Error cargando datos");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        if (eventId) load();
        else {
            setError(lang === "en" ? "Missing eventId" : "Falta eventId");
            setLoading(false);
        }
        return () => {
            cancelled = true;
        };
    }, [eventId, lang]);

    // Cuando hay datos y no hay error, esperamos a que carguen las imágenes dentro de .sheet
    useEffect(() => {
        if (loading || error) return;
        const sheet = document.querySelector(".sheet");
        if (!sheet) return;

        const imgs = Array.from(sheet.querySelectorAll("img"));
        if (imgs.length === 0) {
            setAssetsReady(true);
            return;
        }

        let pending = imgs.length;
        const done = () => {
            pending -= 1;
            if (pending <= 0) setAssetsReady(true);
        };

        imgs.forEach((img) => {
            if (img.complete && img.naturalWidth > 0) {
                done();
            } else {
                img.addEventListener("load", done, { once: true });
                img.addEventListener("error", done, { once: true });
            }
        });

        return () => {
            imgs.forEach((img) => {
                img.onload = null;
                img.onerror = null;
            });
        };
    }, [loading, error, eventId, menuId]);

    // Lanza impresión solo cuando datos + imágenes están listos
    useEffect(() => {
        if (!loading && !error && assetsReady) {
            const t = setTimeout(() => window.print(), 120);
            const after = () => window.close?.();
            window.addEventListener("afterprint", after, { once: true });
            return () => {
                clearTimeout(t);
                window.removeEventListener("afterprint", after);
            };
        }
    }, [loading, error, assetsReady]);

    // Devuelve el texto del alérgeno según idioma.
    function getAllergenText(a, lang) {
        const esTxt = (a.alergenoDescripcion || a.alergeno || "").trim();
        const enTxt = (a.alergenoDescripcion_en || a.alergeno_en || "").trim();

        if (lang === "en") {
            if (enTxt) return enTxt;
            const key = norm(esTxt || a.alergeno || a.alergenoDescripcion);
            return ALLERGEN_EN_MAP[key] || esTxt || "";
        }
        return esTxt || enTxt || "";
    }

    // === MOD: calcula campos en ambos idiomas + vinos ===
    const menu = useMemo(() => {
        if (!evento) return null;
        const m = (evento.menus || []).find((x) => String(x.id) === String(menuId));
        if (!m) return null;

        // Platos
        const platos = (m.platos || []).map((p, idx, arr) => {
            const { es: nombre_es, en: nombre_en } = pickBoth(p, "nombre");
            const { es: desc_es, en: desc_en } = pickBoth(p, "descripcion");

            const alergenos_es = (p.alergenos || []).map((a) => getAllergenText(a, "es")).filter(Boolean);
            const alergenos_en = (p.alergenos || []).map((a) => getAllergenText(a, "en")).filter(Boolean);

            const { es: grupo_es, en: grupo_en } = groupForBoth(p, idx, arr.length);

            return {
                ...p,
                _desc_es: desc_es.trim(),
                _desc_en: desc_en.trim(),
                _alergenos_es: alergenos_es,
                _alergenos_en: alergenos_en,
                _grupo_es: grupo_es,
                _grupo_en: grupo_en,
            };
        });

        // Vinos: soporta objetos o ids
        const vinos = (() => {
            // Caso A: objetos
            const vinosObj = Array.isArray(m.vinos) ? m.vinos : [];

            // Caso B: ids (por si backend guarda ids)
            const ids =
                Array.isArray(m.vinoIds) ? m.vinoIds :
                    Array.isArray(m.vinosIds) ? m.vinosIds :
                        [];

            if (vinosObj.length) {
                return vinosObj.map((v) => {
                    const { es: nombre_es, en: nombre_en } = pickBoth(v, "nombre");
                    const { es: tipo_es, en: tipo_en } = pickBoth(v, "tipo");
                    const { es: desc_es, en: desc_en } = pickBoth(v, "descripcion");

                    return {
                        id: v.id,
                        _nombre_es: (nombre_es || "").trim(),
                        _nombre_en: (nombre_en || nombre_es || "").trim(),
                        _tipo_es: (tipo_es || "").trim(),
                        _tipo_en: (tipo_en || tipo_es || "").trim(),
                        _desc_es: (desc_es || "").trim(),
                        _desc_en: (desc_en || desc_es || "").trim(),
                    };
                });
            }

            if (ids.length) {
                return ids.map((id) => ({
                    id,
                    _nombre_es: `Vino #${id}`,
                    _nombre_en: `Wine #${id}`,
                    _tipo_es: "",
                    _tipo_en: "",
                    _desc_es: "",
                    _desc_en: "",
                }));
            }

            return [];
        })();

        const { es: nombreMenu_es, en: nombreMenu_en } = pickBoth(m, "nombre");

        return {
            ...m,
            _nombreMenu_es: (nombreMenu_es || m.descripcion || "").trim(),
            _nombreMenu_en: (nombreMenu_en || nombreMenu_es || m.descripcion || "").trim(),
            _platos: platos,
            _vinos: vinos,
        };
    }, [evento, menuId]); // eslint-disable-line react-hooks/exhaustive-deps

    // === MOD: agrupación por nombre ES pero mostrando ES+EN ===
    const grupos = useMemo(() => {
        if (!menu) return [];
        const map = new Map(); // clave ES
        for (const p of menu._platos) {
            const key = p._grupo_es || "Platos";
            if (!map.has(key)) map.set(key, { es: p._grupo_es, en: p._grupo_en, platos: [] });
            map.get(key).platos.push(p);
        }

        const orderES = ["Primer plato", "Segundo plato", "Postre"];
        const keys = Array.from(map.keys()).sort((a, b) => {
            const ia = orderES.findIndex((k) => a.toLowerCase().includes(k.toLowerCase()));
            const ib = orderES.findIndex((k) => b.toLowerCase().includes(k.toLowerCase()));
            if (ia === -1 && ib === -1) return a.localeCompare(b);
            if (ia === -1) return 1;
            if (ib === -1) return -1;
            return ia - ib;
        });

        return keys.map((k) => ({
            nombre_es: map.get(k).es,
            nombre_en: map.get(k).en,
            platos: map.get(k).platos,
        }));
    }, [menu]);

    const BRAND = { name: "LOS MELONARES", monogram: "M" };

    if (loading)
        return (
            <div className="print-wrap luxe">
                <div className="no-print preloader">{lang === "en" ? "Loading…" : "Cargando…"}</div>
            </div>
        );

    if (error || !menu)
        return (
            <div className="print-wrap luxe">
                <div className="no-print preloader danger">
                    {error || (lang === "en" ? "Menu not found" : "Menú no encontrado")}
                </div>
            </div>
        );
    const getGroup = (needleEs) =>
        grupos.find((g) => (g.nombre_es || "").toLowerCase().includes(needleEs));

    const gPrimer = getGroup("primer plato");
    const gSegundo = getGroup("segundo plato");
    const gPostre = getGroup("postre");

    return (
        <div className="print-wrap luxe">
            <div className="sheet frame">
                {/* cabecera marca (fila 1) */}
                <header className="brand">
                    {logoFailed ? (
                        <>
                            <div className="monogram">{BRAND.monogram}</div>
                            <div className="brand-name">{BRAND.name}</div>
                        </>
                    ) : (
                        <img
                            className="brand-logo"
                            alt="Los Melonares"
                            src={logo}
                            onError={() => setLogoFailed(true)}
                            style={{ width: "40mm", height: "auto", display: "block" }}
                        />
                    )}
                </header>

                {/* cuerpo centrado (fila 2) */}
                <main className="sheet-body">
                    <div className="menu-main">
                        <h1 className="menu-title">
                            <span className="es">{menu?._nombreMenu_es || "Menú"}</span>
                        </h1>

                        {/* GRID 2x2 */}
                        {/* Primer plato */}
                        <section className="cell">
                            {gPrimer ? (
                                <>
                                    <h2 className="course-title bilingual-slash">
                                        <span className="es">{gPrimer.nombre_es}</span>
                                        <span className="slash"> / </span>
                                        <span className="en">{gPrimer.nombre_en}</span>
                                    </h2>

                                    <div className="lang-block es">
                                        {gPrimer.platos.map((p, i) => (
                                            <div key={`p1-es-${p.id ?? i}`} className="dish-block">
                                                {p._desc_es ? <div className="dish-name-es">{p._desc_es}</div> : null}
                                                {i < gPrimer.platos.length - 1 && <div className="mini-divider" />}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="lang-block en">
                                        {gPrimer.platos.map((p, i) => (
                                            <div key={`p1-en-${p.id ?? i}`} className="dish-block">
                                                {p._desc_en ? <div className="dish-name-en">{p._desc_en}</div> : null}
                                                {i < gPrimer.platos.length - 1 && <div className="mini-divider" />}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </section>

                        {/* Segundo plato */}
                        <section className="cell">
                            {gSegundo ? (
                                <>
                                    <h2 className="course-title bilingual-slash">
                                        <span className="es">{gSegundo.nombre_es}</span>
                                        <span className="slash"> / </span>
                                        <span className="en">{gSegundo.nombre_en}</span>
                                    </h2>

                                    <div className="lang-block es">
                                        {gSegundo.platos.map((p, i) => (
                                            <div key={`p2-es-${p.id ?? i}`} className="dish-block">
                                                {p._desc_es ? <div className="dish-name-es">{p._desc_es}</div> : null}
                                                {i < gSegundo.platos.length - 1 && <div className="mini-divider" />}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="lang-block en">
                                        {gSegundo.platos.map((p, i) => (
                                            <div key={`p2-en-${p.id ?? i}`} className="dish-block">
                                                {p._desc_en ? <div className="dish-name-en">{p._desc_en}</div> : null}
                                                {i < gSegundo.platos.length - 1 && <div className="mini-divider" />}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </section>

                        {/* Postre */}
                        <section className="cell">
                            {gPostre ? (
                                <>
                                    <h2 className="course-title bilingual-slash">
                                        <span className="es">{gPostre.nombre_es}</span>
                                        <span className="slash"> / </span>
                                        <span className="en">{gPostre.nombre_en}</span>
                                    </h2>

                                    <div className="lang-block es">
                                        {gPostre.platos.map((p, i) => (
                                            <div key={`p3-es-${p.id ?? i}`} className="dish-block">
                                                {p._desc_es ? <div className="dish-name-es">{p._desc_es}</div> : null}
                                                {i < gPostre.platos.length - 1 && <div className="mini-divider" />}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="lang-block en">
                                        {gPostre.platos.map((p, i) => (
                                            <div key={`p3-en-${p.id ?? i}`} className="dish-block">
                                                {p._desc_en ? <div className="dish-name-en">{p._desc_en}</div> : null}
                                                {i < gPostre.platos.length - 1 && <div className="mini-divider" />}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : null}
                        </section>

                        {/* Vinos */}
                        <section className="cell">
                            <h2 className="course-title bilingual-slash">
                                <span className="es">Vinos</span>
                                <span className="slash"> / </span>
                                <span className="en">Wines</span>
                            </h2>

                            {menu._vinos?.length ? (
                                <div className="wine-list bilingual-columns">
                                    <div className="lang-block es">
                                        {menu._vinos.map((v) => (
                                            <div key={`ves-${v.id}`} className="wine-row">
                                                <div className="wine-name">{v._nombre_es}</div>
                                                {(v._tipo_es || v._desc_es) && (
                                                    <div className="wine-meta">{[v._tipo_es, v._desc_es].filter(Boolean).join(" · ")}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* <div className="lang-block en">
                                        {menu._vinos.map((v) => (
                                            <div key={`ven-${v.id}`} className="wine-row">
                                                <div className="wine-name">{v._nombre_en}</div>
                                                {(v._tipo_en || v._desc_en) && (
                                                    <div className="wine-meta">{[v._tipo_en, v._desc_en].filter(Boolean).join(" · ")}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div> */}
                                </div>
                            ) : (
                                <div className="footer-muted">{lang === "en" ? "No wines selected." : "Sin vinos seleccionados."}</div>
                            )}
                        </section>
                    </div>

                    {/* FOOTER: alérgenos centrados */}
                    <footer className="allergens-footer">
                        <div className="footer-title bilingual-slash">
                            <span className="es">Alérgenos</span>
                            <span className="slash"> / </span>
                            <span className="en">Allergens</span>
                        </div>

                        <div className="allergens-footer-cols">
                            <div className="lang-block es">
                                <div className="allergens-text">
                                    {(() => {
                                        const list = Array.from(new Set(menu._platos.flatMap((p) => p._alergenos_es).filter(Boolean)));
                                        return list.length ? list.join(" • ") : "Ninguno";
                                    })()}
                                </div>
                            </div>

                            <div className="lang-block en">
                                <div className="allergens-text">
                                    {(() => {
                                        const list = Array.from(new Set(menu._platos.flatMap((p) => p._alergenos_en).filter(Boolean)));
                                        return list.length ? list.join(" • ") : "None";
                                    })()}
                                </div>
                            </div>
                        </div>
                    </footer>
                </main>

            </div>
        </div>
    );
}
